import { z } from 'zod';

// === Tier 1: Enums ==========================================================

// Wire values — the events service path segment, not display names.
export const TrailSourceSchema = z.enum(['bankfeed', 'pay']);
export type TrailSource = z.infer<typeof TrailSourceSchema>;

// === Tier 2: Entities =======================================================

// Unknown or malformed links resolve to null: the event is shown, not clickable.
export const TrailLinkSchema = z
  .discriminatedUnion('type', [
    z.object({
      type: z.literal('payment'),
      source: z.object({ payment_id: z.string().min(1) }),
    }),
  ])
  .transform((raw) => ({ type: raw.type, paymentId: raw.source.payment_id }));
export type TrailLink = z.infer<typeof TrailLinkSchema>;

const toTrailLink = (raw: unknown): TrailLink | null => {
  const parsed = TrailLinkSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

export const TrailEventSchema = z
  .object({
    id: z.string(),
    timestamp: z.string(),
    user_id: z.string().nullish(),
    username: z.string().nullish(),
    user_full_name: z.string().nullish(),
    action: z.string(),
    message: z.string(),
    details: z.string().nullish(),
    link: z.unknown().nullish(),
  })
  .transform((raw) => ({
    id: raw.id,
    timestamp: raw.timestamp,
    userId: raw.user_id ?? null,
    username: raw.username ?? null,
    userFullName: raw.user_full_name ?? null,
    action: raw.action,
    message: raw.message,
    details: raw.details ?? null,
    link: toTrailLink(raw.link),
  }));
export type TrailEvent = z.infer<typeof TrailEventSchema>;

export const TrailFilterOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  picture: z.string().nullish(),
});
export type TrailFilterOption = z.infer<typeof TrailFilterOptionSchema>;

// === Tier 3: Per-endpoint ===================================================

export const TrailSourcePathSchema = z.object({ source: TrailSourceSchema });

export const TrailListQuerySchema = z.object({
  page: z.string().optional(),
  limit: z.number().int().positive(),
  payment_id: z.string().optional(),
  users: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
  texts: z.array(z.string()).optional(),
  timestamp_from: z.string().optional(),
  timestamp_to: z.string().optional(),
});

export const TrailListResponseSchema = z
  .object({
    items: z.array(TrailEventSchema),
    next_page: z.string().nullish(),
    event_hash: z.string().nullish(),
  })
  .transform((raw) => ({
    items: raw.items,
    nextPage: raw.next_page ?? null,
    eventHash: raw.event_hash ?? null,
  }));

export const TrailNewEventsQuerySchema = z.object({
  event_hash: z.string(),
  payment_id: z.string().optional(),
});

export const TrailNewEventsResponseSchema = z.object({ count: z.number() });

export const TrailFiltersResponseSchema = z.object({
  types: z.array(TrailFilterOptionSchema),
  users: z.array(TrailFilterOptionSchema),
});
